import useScrollToTag from 'hooks/useScrollToTag'
import { createPortal } from 'react-dom'
import useArrowsNavigation from 'hooks/useArrowsNavigation'
import { KeyboardEvent, UIEvent, useRef, ChangeEvent, FC } from 'react'

import AutocompleteRow from './AutocompleteRow'
import useAutoComplete, {
  Field,
  UseAutoCompleteProps,
} from './AutocompleteRow/useAutocomplete'

type FieldProps = {
  handleScroll: (evt: UIEvent<Field>) => void
  handleChangeWithHashtagAutocomplete: (evt: ChangeEvent<Field>) => void
}

type AutocompleteFieldProps = UseAutoCompleteProps & {
  onFocus?(): void
  onKeyDown?(e: KeyboardEvent<Field>): void
  isForm?: boolean
  rowsCount?: number
  autoFocus?: boolean
  className?: string
  placeholder?: string
  userMentionSearchScope?: {
    groupId?: string
  }
  renderField: (props: FieldProps) => React.ReactNode
}

const AutocompleteField: FC<AutocompleteFieldProps> = ({
  inputRef,
  text,
  autoFocus,
  setText,
  onChange,
  userMentionSearchScope,
  renderField,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const [
    partHash,
    position,
    autocompleteListFiltered,
    canShowAutocomplete,
    handleSelectHashtag,
    handleCloseAutocomplete,
    handleChangeWithHashtagAutocomplete,
  ] = useAutoComplete({
    text,
    inputRef,
    setText,
    onChange,
    userMentionSearchScope,
  })

  const [activeTag, setActiveTag] = useScrollToTag({
    isVisibleAutocomplete: canShowAutocomplete,
    focusInput: () => autoFocus && inputRef.current?.focus(),
    itemHeight: partHash.value[0] === '@' ? 57 : 38,
  })

  useArrowsNavigation({
    activeTag,
    skip: !canShowAutocomplete,
    searchData: autocompleteListFiltered,
    onClose: handleCloseAutocomplete,
    onSelectTag: handleSelectHashtag,
    onUpdateActiveTag: setActiveTag,
  })

  const overlayRef = useRef<HTMLDivElement | null>(null)

  const handleScroll = (evt: UIEvent<Field>) => {
    const el = evt.target as HTMLTextAreaElement
    overlayRef.current?.scrollTo({ top: el.scrollTop })
  }

  const ref = useRef<HTMLDivElement | null>(null)
  const rect = ref.current?.getBoundingClientRect()

  return (
    <div className="relative w-full p-0" ref={ref}>
      {canShowAutocomplete &&
        createPortal(
          <div
            className="z-tooltip pointer-events-auto fixed left-0 top-0 h-full w-full"
            onClick={handleCloseAutocomplete}
          >
            <div
              ref={wrapperRef}
              id="tag-autocomplete"
              style={{
                ...(position || {}),
                position: 'absolute',
                top: (position?.top || 0) + (rect?.top || 0),
                left: (position?.left || 0) + (rect?.left || 0),
              }}
              className="fade-in no-scrollbar animate-in p-xxs pointer-events-auto flex max-h-[288px] min-w-[112px] max-w-[273px] flex-col gap-0.5 overflow-y-auto rounded-[6px] border border-gray-200 bg-white shadow-[1px_0px_16px_0px_var(--alpha-black-10)]"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
            >
              {autocompleteListFiltered?.map((tag, index) => (
                <AutocompleteRow
                  key={tag.name}
                  {...tag}
                  partHash={partHash.value}
                  isSelected={activeTag === index}
                  onSelectTag={handleSelectHashtag}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
      {renderField({
        handleScroll,
        handleChangeWithHashtagAutocomplete,
      })}
    </div>
  )
}

export default AutocompleteField
